import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { Injectable } from '@angular/core';
import {
  catchError,
  first,
  firstValueFrom,
  map,
  from,
  Observable,
  of,
  switchMap,
  throwError,
  BehaviorSubject,
  finalize,
  mergeMap,
  withLatestFrom,
  forkJoin,
  toArray,
} from 'rxjs';
import {
  AppName,
  EpGenericAppFormProps,
  EpModalService,
  DELETE_RESULT,
  UPLOAD_ERROR_CODE,
  EpGenericAppFormComponent,
  EpTranslateService,
} from '@ep-shared-module/ep-generic-app-form';
import { GenericAppFormIcons, GenericAppFormIconProps } from '@app/shared/models/generic-app';
import { AzureBlobStorageService, FileDetailsProps } from '@app/shared/services/api/azure-blob-storage.service';
import { FeaturePayload, FeaturePermission, Permission, PermissionService } from '@app/shared/services/api/permission.service';
import { AuthService } from '@app/shared/services/auth.service';
import { environment } from '@env/environment';
import { ConfirmationDialogComponent } from '@app/shared/dialog/confirmation-dialog/confirmation-dialog.component';
import { FileUtility } from '@app/shared/file-utility';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ExterneAppsIconStateService } from './externe-apps-icon-state.service';
import { ExterneAppsStateService } from './externe-apps-state.service';
import { TranslateService } from '@ngx-translate/core';
import { FEATURE_FUNCTION, GERMAN_LOCALE, GERMAN_LOCALE_NAME, ModalTransaction } from '@app/shared/constants';
import { LinkAction, MunicipalityManagementQueueStateService, RestrictionAction } from './municipality-management-queue-state.service';
import { ActionType } from '@app/shared/enums';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorDialogComponent } from '@app/shared/dialog/error-dialog/error-dialog.component';
import { IErrorDialogComponent } from '@app/shared/models/dialog/error-dialog';
import { Role, RoleService } from '@app/shared/services/api/role.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { MunicipalityManagementConfigurationStore } from './municipality-management-configuration.store';

@Injectable()
export class ExterneAppFeatureService {
  public savingError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private matDialog: MatDialog,
    private readonly epTranslateService: EpTranslateService,
    private translateService: TranslateService,
    private epGenericAppModalService: EpModalService,
    private azureBlobStorageService: AzureBlobStorageService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private externeAppsStateService: ExterneAppsStateService,
    private externeAppsIconStateService: ExterneAppsIconStateService,
    private municipalityManagementQueueStateService: MunicipalityManagementQueueStateService,
    private overlayLoaderService: OverlayLoaderService,
    private dialog: MatDialog,
    private roleService: RoleService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private municipalityManagementConfigurationStore: MunicipalityManagementConfigurationStore
  ) {
    this.epTranslateService.setLibTranslations('de');
  }

  public getSVGCode(fileName: string, isPreset: boolean): Observable<GenericAppFormIcons> {
    if (isPreset) {
      return this.azureBlobStorageService.getSvgFilePreset(`${fileName}`);
    } else {
      return this.azureBlobStorageService.getSvgFileUpload(`${fileName}`);
    }
  }

  public addExterneApp(): void {
    this.epGenericAppModalService.show(EpGenericAppFormComponent);
    this.showCreateGenericApp();
  }

  public editExterneApp(feature: FeaturePermission): void {
    this.epGenericAppModalService.show(EpGenericAppFormComponent);
    this.externeAppsIconStateService.featureIcons$.pipe(first(), withLatestFrom(this.externeAppsStateService.iconList$)).subscribe({
      next: ([[presetIcons, uploadedIcons], usedIcons]) => {
        uploadedIcons = this.getUploadedIcons(uploadedIcons, usedIcons);
        const allIcons: GenericAppFormIconProps[] = [...presetIcons, ...uploadedIcons];
        const { selectedIcon, selectedHoverIcon } = this.getSelectedIcons(feature.iconName, allIcons);
        const epGenericAppFormData: EpGenericAppFormProps = this.createFormData(
          {
            feature: cloneDeep(feature),
            presetIcons,
            uploadedIcons,
            selectedIcon,
            selectedHoverIcon,
          },
          ModalTransaction.EDIT
        );
        this.epGenericAppModalService.show(EpGenericAppFormComponent, epGenericAppFormData);
      },
    });
  }

  public applyIndividualLinks(municipalityId: string): Observable<any> {
    return this.municipalityManagementQueueStateService.individualLinks$.pipe(
      first(),
      switchMap((permissions) => {
        if (permissions.length > 0) {
          this.municipalityManagementConfigurationStore.updatedExterneUrl = true;
          return from(permissions).pipe(
            mergeMap((permission) => {
              if (permission.action === LinkAction.ADD) {
                return this.permissionService
                  .addIndividualLink(municipalityId, permission.featurePermission.guid, permission.link, this.authService.currentUser.guid)
                  .pipe(first());
              } else if (permission.action === LinkAction.UPDATE) {
                return this.permissionService
                  .updateIndividualLink(municipalityId, permission.featurePermission.guid, permission.link, this.authService.currentUser.guid)
                  .pipe(first());
              } else return of([]);
            }),
            catchError((error) => {
              this.savingError$.next(true);
              return of([]);
            }),
            finalize(() => {
              this.municipalityManagementQueueStateService.clearIndividualLinks();
            })
          );
        } else {
          return of([]);
        }
      })
    );
  }

  private createFormData(data: GenericAppFormData, modalTransaction: ModalTransaction): EpGenericAppFormProps {
    const epGenericAppModalService = this.epGenericAppModalService;
    const overlayLoader = this.overlayLoaderService;
    const { feature, presetIcons, uploadedIcons, selectedIcon, selectedHoverIcon } = data;
    const epGenericAppFormData: EpGenericAppFormProps | any = {
      appName: [
        {
          language: GERMAN_LOCALE,
          name: GERMAN_LOCALE_NAME,
          value: feature.germanName,
        },
      ],
      isTokenRequired: feature.isTokenRequired,
      showTokenRequired: true,
      genericExternalLink: feature.genericExternalURL,
      isEditForm: modalTransaction === ModalTransaction.EDIT,
      isGenericLink: feature.isGenericLink,
      isIframe: feature.featureFunction === FEATURE_FUNCTION.page,
      isPresetTabActive: feature.iconName.startsWith('preset'),
      hoverIconName: selectedHoverIcon,
      presetIcons: presetIcons,
      selectedIcon: selectedIcon,
      title: '',
      uploadedIcons: uploadedIcons,
      getSvgCode: (fileName: string, isPreset: boolean): Promise<any> => this.getSVGIconCode(fileName, isPreset),
      onClose: () => epGenericAppModalService.close(),
      onIconDelete: async (fileName: string): Promise<any> => this.deleteIcon(fileName),
      onIconUpload: (file: File): Promise<any> => this.handleUploadSVG(file, modalTransaction, data),
      onSave: () => this.save(epGenericAppFormData, modalTransaction, overlayLoader, feature),
    };
    return epGenericAppFormData;
  }

  private createFeaturePayload(
    data: EpGenericAppFormProps,
    germanName: string,
    iconName: string,
    hoverIconName: string,
    isCreate: boolean = true
  ): FeaturePayload {
    let payload: FeaturePayload = {
      germanName,
      iconName: `${iconName};${hoverIconName}`,
      featureFunction: data.isIframe ? FEATURE_FUNCTION.page : FEATURE_FUNCTION.tab,
      modifyUser: this.authService.currentUser.guid,
      genericExternalURL: data.genericExternalLink,
      isGenericLink: data.isGenericLink,
      isTokenRequired: data.isTokenRequired ?? false,
    };
    if (isCreate) {
      payload.name = `${Date.now()}_${germanName.substring(0, 36)}`;
    }
    return payload;
  }

  private showCreateGenericApp(): void {
    this.externeAppsIconStateService.featureIcons$
      .pipe(first(), withLatestFrom(this.externeAppsStateService.iconList$))
      .subscribe(([[presetIcons, uploadedIcons], usedIcons]) => {
        const overlayLoader = this.overlayLoaderService;
        const epGenericAppModalService = this.epGenericAppModalService;
        let epGenericAppFormData: EpGenericAppFormProps = {
          ...cloneDeep(initialGenericAppFeature),
          presetIcons: presetIcons,
          uploadedIcons: this.getUploadedIcons(uploadedIcons, usedIcons),
          selectedIcon: presetIcons[0],
          onClose: () => epGenericAppModalService.close(),
          onSave: () => this.save(epGenericAppFormData, ModalTransaction.CREATE, overlayLoader),
          onIconDelete: async (fileName: string): Promise<any> => this.deleteIcon(fileName),
          getSvgCode: (fileName: string, isPreset: boolean): Promise<any> => this.getSVGIconCode(fileName, isPreset),
          onIconUpload: (file: File): Promise<any> => this.handleUploadSVG(file, ModalTransaction.CREATE),
        };
        this.epGenericAppModalService.show(EpGenericAppFormComponent, epGenericAppFormData);
      });
  }

  private getUploadedIcons(icons: GenericAppFormIconProps[], usedIcons: string[]): GenericAppFormIconProps[] {
    return icons.map((icon) => {
      icon.deletable = !usedIcons.includes(`upload_${icon.fileName}`);
      return icon;
    });
  }

  private save(data: EpGenericAppFormProps, modalTransaction: ModalTransaction, overlayLoader: OverlayLoaderService, feature?: FeaturePermission): void {
    const translationModule = `module.userProfile.generalConfiguration.${modalTransaction === ModalTransaction.CREATE ? 'confirmation' : 'editApp'}.`;
    const germanName = data.appName.find((name: any) => name.language === 'de')?.value ?? '';
    const iconName = this.getIconName(data.selectedIcon);
    const hoverIconName = data.hoverIconName ? this.getIconName(data.hoverIconName) : '';
    const payload = this.createFeaturePayload(data, germanName, iconName, hoverIconName, modalTransaction === ModalTransaction.CREATE);
    modalTransaction === ModalTransaction.CREATE
      ? this.createFeature(payload, translationModule, overlayLoader)
      : this.updateFeature(feature ?? ({} as FeaturePermission), payload, translationModule, overlayLoader);
    this.municipalityManagementConfigurationStore.updatedExterneUrl = true;
  }

  private createFeature(payload: FeaturePayload, translationModule: string, overlayLoader: OverlayLoaderService): void {
    let featurePermission = {} as FeaturePermission;
    overlayLoader.isLoading = true;
    this.permissionService
      .createFeature(payload)
      .pipe(
        first(),
        mergeMap((featurePermission: FeaturePermission) => this.addAllRolestoPermission(featurePermission)),
        mergeMap((featurePermission) =>
          forkJoin([
            this.permissionService.assignRestrictions(
              [{ permissionId: featurePermission.guid, action: RestrictionAction.DEACTIVATE }],
              null,
              this.authService.currentUser.guid
            ),
            of(featurePermission),
          ])
        ),
        mergeMap(([restriction, featurePermission]) =>
          this.permissionService.getFeaturePermissons(this.userMunicipalitiesService.currentMunicipality.guid).pipe(
            map((updatedPermissions) => {
              return updatedPermissions.find((permission) => permission.guid === featurePermission.guid);
            })
          )
        ),
        catchError((err: HttpErrorResponse | Error) => this.handleServerError(err, featurePermission))
      )
      .subscribe({
        next: (resp) => {
          this.epGenericAppModalService.close();
          overlayLoader.isLoading = false;
          this.externeAppsStateService.addApp(resp as FeaturePermission);
          this.openConfirmationDialog(translationModule, 'success');
        },
        error: () => this.showServerError(overlayLoader),
      });
  }

  private addAllRolestoPermission(featurePermission: FeaturePermission): Observable<FeaturePermission> {
    return this.roleService.getAll().pipe(
      mergeMap((roles: Role[]): Observable<Role> => from(roles)),
      mergeMap((role) => this.roleService.postRolePermissionExterneApp(featurePermission.guid, role.guid, this.authService.currentUser.guid)),
      toArray(),
      map(() => featurePermission)
    );
  }

  private handleServerError(err: HttpErrorResponse | Error, featurePermission?: FeaturePermission): Observable<never> {
    if (!isEmpty(featurePermission)) {
      this.permissionService.deleteFeature(featurePermission).pipe(first()).subscribe();
    }
    return throwError(() => err);
  }

  private showServerError(overlayLoader: OverlayLoaderService): void {
    overlayLoader.isLoading = false;
    this.epGenericAppModalService.close();

    this.dialog.open<ErrorDialogComponent, IErrorDialogComponent, unknown>(ErrorDialogComponent, {
      data: this.getServerErrorTranslation(),
      panelClass: ['ep-generic-dialog'],
      disableClose: true,
    });
  }

  private updateFeature(feature: FeaturePermission, payload: FeaturePayload, translationModule: string, overlayLoader: OverlayLoaderService): void {
    this.matDialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translateService.instant(translationModule + 'title'),
          description: this.translateService.instant(translationModule + 'description'),
          confirmText: this.translateService.instant(translationModule + 'confirmText'),
          declineText: this.translateService.instant(translationModule + 'cancelText'),
        },
        panelClass: ['ep-generic-dialog'],
        disableClose: true,
      })
      .afterClosed()
      .subscribe({
        next: (response) => {
          if (response) {
            this.epGenericAppModalService.close();
            overlayLoader.isLoading = true;
            this.permissionService
              .updateFeature(feature, payload)
              .pipe(
                first(),
                switchMap((permission) => this.updateByExterneType(feature as FeaturePermission, payload, permission as Permission)),
                switchMap((_) =>
                  this.permissionService.getAllRestrictionsByPermissionId(feature.guid).pipe(
                    map((updatedPermissions) => {
                      return updatedPermissions.find((permission) => permission.guid === feature.guid);
                    })
                  )
                )
              )
              .subscribe({
                next: (featurePermission) => {
                  if (featurePermission) {
                    overlayLoader.isLoading = false;
                    this.externeAppsStateService.updateApp(featurePermission as FeaturePermission);
                    this.openConfirmationDialog(translationModule + 'successModal.', 'success');
                  } else {
                    this.showServerError(overlayLoader);
                  }
                },
                error: () => this.showServerError(overlayLoader),
              });
          }
        },
        error: () => this.showServerError(overlayLoader),
      });
  }

  private updateByExterneType(feature: FeaturePermission, payload: FeaturePayload, permission: Permission): Observable<FeaturePermission | undefined> {
    const isGenericToIndividual = feature.isGenericLink && !payload.isGenericLink;
    if (isGenericToIndividual) {
      return this.permissionService
        .assignRestrictions([{ permissionId: feature.guid, action: RestrictionAction.DEACTIVATE }], null, this.authService.currentUser.guid)
        .pipe(first());
    } else {
      return of(permission as FeaturePermission);
    }
  }

  public deleteExterneApp(feature: FeaturePermission): void {
    const translationModule = `module.userProfile.generalConfiguration.deleteApp.`;
    const actionType = ActionType.delete;

    this.epGenericAppModalService.close();
    this.matDialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translateService.instant(translationModule + 'title'),
          actionType,
          description: this.translateService.instant(translationModule + 'description'),
          confirmText: this.translateService.instant(translationModule + 'deleteText'),
          declineText: this.translateService.instant(translationModule + 'cancelText'),
        },
        panelClass: ['ep-generic-dialog'],
        disableClose: true,
      })
      .afterClosed()
      .subscribe((response: boolean) => {
        if (response) {
          this.overlayLoaderService.isLoading = true;
          this.permissionService
            .deleteFeature(feature)
            .pipe(
              first(),
              map(() => this.externeAppsStateService.deleteApp(feature as FeaturePermission))
            )
            .subscribe({
              next: () => {
                this.overlayLoaderService.isLoading = false;
                this.openConfirmationDialog(translationModule + 'successModal.', 'success');
              },
              error: () => this.showServerError(this.overlayLoaderService),
            });
        }
      });
  }

  private async deleteIcon(fileName: string): Promise<{ result: DELETE_RESULT }> {
    // TODO Stromboli add validation here when the icon is being used by other apps and return DENIED result
    // return {result: DELETE_RESULT.DENIED}

    let result = DELETE_RESULT.ERROR;
    let res;
    try {
      res = await firstValueFrom(this.azureBlobStorageService.deleteSvgFileUpload(fileName).pipe(catchError((error) => error.error.text)));
      if (res) {
        this.externeAppsIconStateService.removeUpload(fileName);
        result = DELETE_RESULT.SUCCESS;
      }
    } catch (e) {
      result = DELETE_RESULT.ERROR;
    } finally {
      return { result: result };
    }
  }

  private getIconName(icon: GenericAppFormIconProps): string {
    return `${icon.isPreset ? 'preset_' : 'upload_'}${FileUtility.getFilename(icon.iconPath)}`;
  }

  private async getSVGIconCode(fileName: string, isPreset: boolean): Promise<string> {
    let iconSVG;
    iconSVG = await firstValueFrom(this.getSVGCode(`${fileName}`, isPreset).pipe(catchError((_) => of(''))));
    return (iconSVG as GenericAppFormIcons)?.svgCode;
  }

  private getSelectedIcons(
    iconName: string,
    allIcons: GenericAppFormIconProps[]
  ): { selectedIcon: GenericAppFormIconProps | undefined; selectedHoverIcon: GenericAppFormIconProps | undefined } {
    const [featureIcon, featureHoverIcon] = iconName.split(';');
    const selectedIcon = allIcons.find((icon) => icon.fileName === featureIcon.split('_').slice(1).join('_'));
    const selectedHoverIcon = allIcons.find((icon) => icon.fileName === featureHoverIcon.split('_').slice(1).join('_'));
    return { selectedIcon, selectedHoverIcon };
  }

  private openConfirmationDialog(translationModule: string, status: string): void {
    this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translateService.instant(translationModule + 'title'),
        status,
        description: this.translateService.instant(translationModule + 'description'),
        confirmText: this.translateService.instant(translationModule + 'confirmText'),
      },
      panelClass: ['ep-generic-dialog'],
      disableClose: true,
    });
  }

  private handleUploadSVG(file: File, modalTransaction: ModalTransaction, selectedFeature?: GenericAppFormData): Promise<any> {
    const formData = new FormData();
    const uniqueFileName = `${Date.now()}_${file.name}`;
    formData.append('file', file, uniqueFileName);

    return from(this.uploadFile(formData))
      .pipe(
        map(async (upload) => {
          await this.postUploadProcessing(upload);
          return upload;
        }),
        catchError((error: HttpErrorResponse) => {
          if (this.isVirusScannerServerError(error)) {
            this.handleVirusScannerServerError(selectedFeature, modalTransaction);
          }
          return of(this.handleError(UPLOAD_ERROR_CODE.OTHERS));
        })
      )
      .toPromise();
  }

  private uploadFile(formData: FormData): Observable<FileDetailsProps> {
    return from(firstValueFrom(this.azureBlobStorageService.uploadSvgFile(formData))).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  private async postUploadProcessing(upload: FileDetailsProps): Promise<void> {
    await firstValueFrom(this.azureBlobStorageService.getSvgFileUpload(upload.fileName));
    upload.iconPath = `${environment.proxyApiURL}/api/icon/upload/${upload.fileName}`;
    this.externeAppsIconStateService.addUpload({
      fileName: upload.fileName,
      iconPath: upload.iconPath,
      fileType: upload.fileType,
      fileSize: upload.fileSize.toString(),
      isLoading: true,
    } as GenericAppFormIconProps);
  }

  private handleError(errorCode: UPLOAD_ERROR_CODE): { hasError: boolean; errorCode: UPLOAD_ERROR_CODE } {
    return { hasError: true, errorCode };
  }

  private isVirusScannerServerError(error: HttpErrorResponse): boolean {
    return error.status === 500 && (error.error.AppErrorCode === 2 || error.error.AppErrorCode === 3);
  }

  private handleVirusScannerServerError(selectedFeature: GenericAppFormData | undefined, modalTransaction: ModalTransaction): void {
    this.epGenericAppModalService.close();
    const dialogRef = this.showGeneralErrorDialog();
    dialogRef.afterClosed().subscribe((result) => {
      if (!result && modalTransaction === ModalTransaction.EDIT && !isNil(selectedFeature)) {
        this.editExterneApp(selectedFeature.feature);
      } else if (!result) {
        this.addExterneApp();
      }
    });
  }

  public showGeneralErrorDialog(): MatDialogRef<ErrorDialogComponent> {
    return this.dialog.open<ErrorDialogComponent, IErrorDialogComponent, unknown>(ErrorDialogComponent, {
      data: this.getVirusScannerTranslation(),
      panelClass: ['ep-generic-dialog'],
      disableClose: true,
    });
  }

  private getVirusScannerTranslation(): IErrorDialogComponent {
    const translationModule = `module.userProfile.generalConfiguration.errorScenario.virusScannerError.`;
    return {
      title: this.translateService.instant(translationModule + 'title'),
      description: this.translateService.instant(translationModule + 'description'),
      buttonText: this.translateService.instant(translationModule + 'buttonText'),
      uploadButtonText: this.translateService.instant(translationModule + 'uploadButtonText'),
    };
  }

  private getServerErrorTranslation(): IErrorDialogComponent {
    const translationModule = `module.userProfile.generalConfiguration.errorScenario.serverError.`;
    return {
      title: this.translateService.instant(translationModule + 'title'),
      description: this.translateService.instant(translationModule + 'description'),
      buttonText: this.translateService.instant(translationModule + 'buttonText'),
    };
  }
}

export const initialGenericAppName: AppName = {
  language: GERMAN_LOCALE,
  name: GERMAN_LOCALE_NAME,
  value: '',
};

export const initialGenericAppFeature: EpGenericAppFormProps = {
  appName: [{ ...initialGenericAppName }],
  defaultLocale: GERMAN_LOCALE,
  title: '',
  isEditForm: false,
  presetIcons: [],
  uploadedIcons: [],
  isPresetTabActive: true,
  isIframe: true,
  isGenericLink: true,
  genericExternalLink: '',
  isTokenRequired: false,
  showTokenRequired: true,
  selectedIcon: { fileName: '', iconPath: '', fileType: '', fileSize: '' },
  hoverIconName: { fileName: '', iconPath: '', fileType: '', fileSize: '' },
};

export interface GenericAppFormData {
  feature: FeaturePermission;
  presetIcons: GenericAppFormIconProps[];
  uploadedIcons: GenericAppFormIconProps[];
  selectedIcon: GenericAppFormIconProps | undefined;
  selectedHoverIcon: GenericAppFormIconProps | undefined;
}
